import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import SuccessAnimation from '../components/SuccessAnimation';

type ToastVariant = 'success' | 'error' | 'info';

interface ToastState {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  success: (message: string, durationMs?: number) => void;
  /** Something failed. Announced as an alert, and it stays up longer so it is not missed. */
  error: (message: string, durationMs?: number) => void;
  info: (message: string, durationMs?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((variant: ToastVariant, message: string, durationMs: number) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, variant }]);
    window.setTimeout(() => dismiss(id), durationMs);
  }, [dismiss]);

  const success = useCallback((message: string, durationMs = 3000) => show('success', message, durationMs), [show]);
  const error = useCallback((message: string, durationMs = 6000) => show('error', message, durationMs), [show]);
  const info = useCallback((message: string, durationMs = 4000) => show('info', message, durationMs), [show]);

  return (
    <ToastContext.Provider value={{ success, error, info }}>
      {children}
      <div className="fixed top-4 right-4 z-[1000] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem: React.FC<{ toast: ToastState; onDismiss: () => void }> = ({ toast, onDismiss }) => {
  const [entering, setEntering] = useState(true);

  useEffect(() => {
    const t = window.setTimeout(() => setEntering(false), 20);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div
      className={`pointer-events-auto flex items-center gap-3 min-w-[280px] max-w-[420px] bg-white border border-slate-200 rounded-lg shadow-lg pl-2 pr-4 py-2 transition-all duration-200 ${
        entering ? 'opacity-0 translate-y-[-8px]' : 'opacity-100 translate-y-0'
      }`}
      role={toast.variant === 'error' ? 'alert' : 'status'}
    >
      {toast.variant === 'success' ? (
        <SuccessAnimation size={40} />
      ) : (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center" aria-hidden="true">
          {toast.variant === 'error' ? (
            <AlertTriangle className="h-5 w-5 text-red-600" />
          ) : (
            <Info className="h-5 w-5 text-primary-500" />
          )}
        </span>
      )}
      {/* pre-line: messages that were browser alerts until 2026-09-25 (D64)
          carry their own line breaks — a list of blocked lines, a reason
          then a remedy — and collapsed onto one line without it. */}
      <p className="flex-1 whitespace-pre-line text-sm text-slate-800">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-slate-400 hover:text-slate-600 text-lg leading-none"
      >
        ×
      </button>
    </div>
  );
};
